import React, { useState, useEffect } from 'react';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import { styleReset } from 'react95';
// Import the theme from the correct path
import original from 'react95/dist/themes/original';
import Desktop from './components/Desktop';
import LoginScreen from './components/LoginScreen';
import { authService, AuthUser } from './services/authService';
import { t } from './data/translations';
import './styles/global.css';

// Apply global styles from react95
const GlobalStyles = createGlobalStyle`
  ${styleReset}
`;

// Boot Screen component for startup animation
const BootScreen: React.FC<{ onComplete: () => void; userName: string }> = ({ onComplete, userName }) => {
  const [bootText, setBootText] = useState(t('boot.initializing'));
  
  useEffect(() => {
    // Simulate boot sequence with changing messages
    const bootSequence = [
      { message: t('boot.initializing'), delay: 1000 },
      { message: t('boot.loadingComponents'), delay: 1500 },
      { message: t('boot.checkingConfig'), delay: 2000 },
      { message: t('boot.welcome', { name: userName }), delay: 3000 },
      { message: t('boot.preparing'), delay: 4000 },
      { message: t('boot.ready'), delay: 5500 },
    ];
    
    let timeout: number;
    
    // Execute boot sequence
    bootSequence.forEach((step, index) => {
      timeout = window.setTimeout(() => {
        setBootText(step.message);
        
        // On last step, call the onComplete callback
        if (index === bootSequence.length - 1) {
          setTimeout(onComplete, 1000);
        }
      }, step.delay);
    });
    
    
    return () => {
      clearTimeout(timeout);
    };
  }, [onComplete, userName]);
  
  return (
    <div className="boot-screen">
      <div className="boot-text">{bootText}</div>
      <div className="progress-bar">
        <div className="progress-fill"></div>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bootComplete, setBootComplete] = useState(false);
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);

  // Check for existing session on app start
  useEffect(() => {
    const existingUser = authService.checkExistingSession();
    if (existingUser) {
      setCurrentUser(existingUser);
      setIsLoggedIn(true);
    }
  }, []);

  const handleLogin = async (name: string, secretKey: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.login(name, secretKey);
      
      if (response.success && response.user) {
        setCurrentUser(response.user);
        setIsLoggedIn(true);
        setBootComplete(false); // Reset boot screen
      } else {
        setError(response.error || t('auth.loginFailed'));
      }
    } catch (err) {
      setError(t('auth.loginFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBootComplete = () => {
    setBootComplete(true);
  };

  const handleLogout = () => {
    authService.logout();
    setIsLoggedIn(false);
    setCurrentUser(null);
    setBootComplete(false);
    setError(null);
  };

  const handleOpenWindow = (windowId: string) => {
    // Простая заглушка для открытия окон
    console.log('Opening window:', windowId);
  };

  return (
    <div className="app-container">
      <GlobalStyles />
      <ThemeProvider theme={original}>
        {!isLoggedIn ? (
          <LoginScreen 
            onLogin={handleLogin}
            isLoading={isLoading}
            error={error || undefined}
          />
        ) : !bootComplete ? (
          <BootScreen 
            onComplete={handleBootComplete}
            userName={currentUser?.name || 'Гость'}
          />
        ) : (
          <Desktop onLogout={handleLogout} currentUser={currentUser} onOpenWindow={handleOpenWindow} />
        )}
      </ThemeProvider>
    </div>
  );
};

export default App;
